import React, { useState, useEffect } from 'react';
import { Search, ArrowRight, Clock, CheckCircle, Shield, Users, Star, TrendingUp, Activity, Heart, Eye, Brain, Stethoscope, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SurgeriesProcedures = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const categories = [
    { id: 'all', name: 'All Procedures', icon: '🏥', color: '#286CFF' },
    { id: 'general', name: 'General Surgery', icon: '🔬', color: '#28C08A' },
    { id: 'orthopedics', name: 'Orthopedics', icon: '🦴', color: '#FFD28A' },
    { id: 'ent', name: 'ENT', icon: '👂', color: '#FF6B6B' },
    { id: 'gynecology', name: 'Gynecology', icon: '👶', color: '#FF6B6B' },
    { id: 'urology', name: 'Urology', icon: '💧', color: '#79A8FF' },
    { id: 'gastro', name: 'Gastroenterology', icon: '🫁', color: '#28C08A' },
    { id: 'cancer', name: 'Cancer Care', icon: '🎗️', color: '#FFD28A' },
    { id: 'eye', name: 'Eye Surgeries', icon: '👁️', color: '#286CFF' },
    { id: 'cosmetic', name: 'Cosmetic & Plastic', icon: '✨', color: '#FF6B6B' },
    { id: 'neuro', name: 'Neurology', icon: '🧠', color: '#79A8FF' },
    { id: 'cardio', name: 'Cardiology', icon: '❤️', color: '#FF6B6B' }
  ];

  const surgeries = [
    {
      id: 1,
      category: 'general',
      name: 'Laparoscopic Cholecystectomy',
      description: 'Minimally invasive gallbladder removal',
      benefits: ['Less pain', 'Faster recovery', 'Minimal scarring'],
      recovery: '1-2 weeks',
      technique: 'Laparoscopic',
      costRange: '₹50,000 - ₹1,50,000',
      successRate: '98%',
      risk: 'Low',
      specialist: 'General Surgeon'
    },
    {
      id: 2,
      category: 'orthopedics',
      name: 'Knee Replacement Surgery',
      description: 'Total knee arthroplasty for joint restoration',
      benefits: ['Pain relief', 'Improved mobility', 'Better quality of life'],
      recovery: '6-8 weeks',
      technique: 'Robotic/Traditional',
      costRange: '₹2,00,000 - ₹4,00,000',
      successRate: '95%',
      risk: 'Medium',
      specialist: 'Orthopedic Surgeon'
    },
    {
      id: 3,
      category: 'eye',
      name: 'LASIK Eye Surgery',
      description: 'Laser vision correction for refractive errors',
      benefits: ['Freedom from glasses', 'Quick procedure', 'Immediate results'],
      recovery: '1-3 days',
      technique: 'Bladeless LASIK',
      costRange: '₹40,000 - ₹1,00,000',
      successRate: '96%',
      risk: 'Low',
      specialist: 'Ophthalmologist'
    },
    {
      id: 4,
      category: 'cardio',
      name: 'Coronary Angioplasty',
      description: 'Opening blocked heart arteries with stents',
      benefits: ['Improved blood flow', 'Quick recovery', 'Life-saving'],
      recovery: '2-4 weeks',
      technique: 'Minimally Invasive',
      costRange: '₹1,50,000 - ₹3,50,000',
      successRate: '94%',
      risk: 'Medium',
      specialist: 'Cardiologist'
    },
    {
      id: 5,
      category: 'gynecology',
      name: 'Hysterectomy',
      description: 'Surgical removal of the uterus',
      benefits: ['Pain relief', 'Treatment completion', 'Improved health'],
      recovery: '4-6 weeks',
      technique: 'Laparoscopic/Open',
      costRange: '₹80,000 - ₹2,00,000',
      successRate: '97%',
      risk: 'Medium',
      specialist: 'Gynecologist'
    },
    {
      id: 6,
      category: 'ent',
      name: 'Tonsillectomy',
      description: 'Removal of tonsils for chronic infections',
      benefits: ['No more infections', 'Better breathing', 'Improved sleep'],
      recovery: '1-2 weeks',
      technique: 'Coblation/Traditional',
      costRange: '₹30,000 - ₹80,000',
      successRate: '99%',
      risk: 'Low',
      specialist: 'ENT Specialist'
    }
  ];

  const patientJourney = [
    { step: 'Online Consultation', icon: '💻', description: 'Connect with specialists via video call' },
    { step: 'Diagnosis & Testing', icon: '🔬', description: 'Comprehensive health assessments' },
    { step: 'Surgery Planning', icon: '📋', description: 'Personalized treatment roadmap' },
    { step: 'Insurance Guidance', icon: '🛡️', description: 'Cost transparency and support' },
    { step: 'Surgical Procedure', icon: '⚕️', description: 'Expert care in advanced facilities' },
    { step: 'Post-op Care', icon: '🏥', description: 'Recovery monitoring and support' },
    { step: 'AI Follow-up', icon: '🤖', description: 'Health Genie tracks your recovery' }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      age: 52,
      procedure: 'Knee Replacement',
      rating: 5,
      comment: 'Life-changing surgery! The team guided me through every step. Back to my morning walks now.',
      image: '👨'
    },
    {
      name: 'Priya Sharma',
      age: 35,
      procedure: 'LASIK Surgery',
      rating: 5,
      comment: 'Best decision ever! No more glasses and the recovery was super quick. Highly recommend!',
      image: '👩'
    },
    {
      name: 'Mohammed Ali',
      age: 45,
      procedure: 'Cardiac Angioplasty',
      rating: 5,
      comment: 'The doctors saved my life. Excellent care from consultation to recovery. Forever grateful!',
      image: '👨'
    }
  ];

  const filteredSurgeries = selectedCategory === 'all' 
    ? surgeries 
    : surgeries.filter(s => s.category === selectedCategory);

  const toggleCompare = (surgery) => {
    if (compareList.find(s => s.id === surgery.id)) {
      setCompareList(compareList.filter(s => s.id !== surgery.id));
    } else if (compareList.length < 3) {
      setCompareList([...compareList, surgery]);
    }
  };

  const responsiveStyles = getResponsiveStyles(isMobile, isSmallMobile);

  return (
    <div style={responsiveStyles.page}>
      <Navbar />
      
      <section style={responsiveStyles.heroSection}>
        <div style={responsiveStyles.container}>
          <div style={responsiveStyles.heroContent}>
            <h1 style={responsiveStyles.heroTitle}>
              Advanced Surgeries & Medical Procedures
            </h1>
            <p style={responsiveStyles.heroSubtitle}>
              Safe, Trusted, and Patient-Centric
            </p>
            <p style={responsiveStyles.heroDescription}>
              Get expert consultation, care coordination, and seamless surgery support—all in one place
            </p>
            
            <div style={responsiveStyles.searchWrapper}>
              <div style={responsiveStyles.searchBar}>
                <Search size={isSmallMobile ? 20 : 24} color="#64748B" />
                <input
                  type="text"
                  placeholder="Search for a surgery or condition..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={responsiveStyles.searchInput}
                />
                <button style={responsiveStyles.searchButton}>Search</button>
              </div>
            </div>
          </div>
        </div>
        
        <div style={responsiveStyles.heroIllustration}>
          <svg viewBox="0 0 600 400" style={{ width: '100%', height: '100%', opacity: 0.1 }}>
            <circle cx="100" cy="100" r="60" fill="#286CFF">
              <animate attributeName="cy" values="100;120;100" dur="4s" repeatCount="indefinite"/>
            </circle>
            <circle cx="500" cy="300" r="80" fill="#28C08A">
              <animate attributeName="cx" values="500;480;500" dur="5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="300" cy="200" r="40" fill="#FFD28A">
              <animate attributeName="r" values="40;50;40" dur="3s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
      </section>

      <section style={responsiveStyles.categoriesSection}>
        <div style={responsiveStyles.container}>
          <h2 style={responsiveStyles.sectionTitle}>Browse by Specialty</h2>
          <p style={responsiveStyles.sectionSubtitle}>Explore our comprehensive range of surgical procedures</p>
          
          <div style={responsiveStyles.categoriesGrid}>
            {categories.map((category) => (
              <div
                key={category.id}
                style={{
                  ...responsiveStyles.categoryCard,
                  background: selectedCategory === category.id ? `${category.color}15` : 'white',
                  borderColor: selectedCategory === category.id ? category.color : '#E2E8F0'
                }}
                onClick={() => setSelectedCategory(category.id)}
              >
                <div style={responsiveStyles.categoryIcon}>{category.icon}</div>
                <div style={responsiveStyles.categoryName}>{category.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={responsiveStyles.surgeriesSection}>
        <div style={responsiveStyles.container}>
          <div style={responsiveStyles.surgeriesHeader}>
            <h2 style={responsiveStyles.sectionTitle}>Available Procedures</h2>
            {compareList.length > 0 && (
              <button 
                style={responsiveStyles.compareBtn}
                onClick={() => setShowCompare(true)}
              >
                Compare Selected ({compareList.length})
              </button>
            )}
          </div>
          
          <div style={responsiveStyles.surgeriesGrid}>
            {filteredSurgeries.map((surgery) => (
              <div key={surgery.id} style={responsiveStyles.surgeryCard}>
                <div style={responsiveStyles.surgeryHeader}>
                  <div>
                    <h3 style={responsiveStyles.surgeryName}>{surgery.name}</h3>
                    <p style={responsiveStyles.surgeryDescription}>{surgery.description}</p>
                  </div>
                  <div style={responsiveStyles.compareCheckbox}>
                    <input
                      type="checkbox"
                      checked={compareList.find(s => s.id === surgery.id) !== undefined}
                      onChange={() => toggleCompare(surgery)}
                      style={responsiveStyles.checkbox}
                    />
                    <span style={responsiveStyles.compareLabel}>Compare</span>
                  </div>
                </div>
                
                <div style={responsiveStyles.surgeryBenefits}>
                  <div style={responsiveStyles.benefitLabel}>Benefits:</div>
                  <div style={responsiveStyles.benefitTags}>
                    {surgery.benefits.map((benefit, idx) => (
                      <span key={idx} style={responsiveStyles.benefitTag}>{benefit}</span>
                    ))}
                  </div>
                </div>
                
                <div style={responsiveStyles.surgeryMeta}>
                  <div style={responsiveStyles.metaItem}>
                    <Clock size={isSmallMobile ? 14 : 16} color="#64748B" />
                    <span>Recovery: {surgery.recovery}</span>
                  </div>
                  <div style={responsiveStyles.metaItem}>
                    <CheckCircle size={isSmallMobile ? 14 : 16} color="#28C08A" />
                    <span>{surgery.successRate} success</span>
                  </div>
                </div>
                
                <button style={responsiveStyles.knowMoreBtn}>
                  Know More <ArrowRight size={isSmallMobile ? 14 : 16} style={{ marginLeft: '8px' }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {showCompare && compareList.length > 0 && (
        <div style={responsiveStyles.modalOverlay} onClick={() => setShowCompare(false)}>
          <div style={responsiveStyles.compareModal} onClick={(e) => e.stopPropagation()}>
            <div style={responsiveStyles.modalHeader}>
              <h2 style={responsiveStyles.modalTitle}>Compare Surgeries</h2>
              <button style={responsiveStyles.closeBtn} onClick={() => setShowCompare(false)}>
                <X size={isSmallMobile ? 20 : 24} />
              </button>
            </div>
            
            <div style={responsiveStyles.compareTable}>
              <table style={responsiveStyles.table}>
                <thead>
                  <tr>
                    <th style={responsiveStyles.tableHeader}>Feature</th>
                    {compareList.map(surgery => (
                      <th key={surgery.id} style={responsiveStyles.tableHeader}>{surgery.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={responsiveStyles.tableCell}>Purpose</td>
                    {compareList.map(surgery => (
                      <td key={surgery.id} style={responsiveStyles.tableCell}>{surgery.description}</td>
                    ))}
                  </tr>
                  <tr>
                    <td style={responsiveStyles.tableCell}>Technique</td>
                    {compareList.map(surgery => (
                      <td key={surgery.id} style={responsiveStyles.tableCell}>{surgery.technique}</td>
                    ))}
                  </tr>
                  <tr>
                    <td style={responsiveStyles.tableCell}>Recovery Time</td>
                    {compareList.map(surgery => (
                      <td key={surgery.id} style={responsiveStyles.tableCell}>{surgery.recovery}</td>
                    ))}
                  </tr>
                  <tr>
                    <td style={responsiveStyles.tableCell}>Cost Range</td>
                    {compareList.map(surgery => (
                      <td key={surgery.id} style={responsiveStyles.tableCell}>{surgery.costRange}</td>
                    ))}
                  </tr>
                  <tr>
                    <td style={responsiveStyles.tableCell}>Success Rate</td>
                    {compareList.map(surgery => (
                      <td key={surgery.id} style={responsiveStyles.tableCell}>{surgery.successRate}</td>
                    ))}
                  </tr>
                  <tr>
                    <td style={responsiveStyles.tableCell}>Risk Level</td>
                    {compareList.map(surgery => (
                      <td key={surgery.id} style={responsiveStyles.tableCell}>{surgery.risk}</td>
                    ))}
                  </tr>
                  <tr>
                    <td style={responsiveStyles.tableCell}>Specialist</td>
                    {compareList.map(surgery => (
                      <td key={surgery.id} style={responsiveStyles.tableCell}>{surgery.specialist}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <section style={responsiveStyles.genieSection}>
        <div style={responsiveStyles.container}>
          <div style={responsiveStyles.genieGrid}>
            <div style={responsiveStyles.genieContent}>
              <div style={responsiveStyles.genieBadge}>AI-POWERED GUIDANCE</div>
              <h2 style={responsiveStyles.genieTitle}>
                Surgery Genie – Your Smart Guidance Tool
              </h2>
              <p style={responsiveStyles.genieDescription}>
                Navigate your surgical journey with confidence using our AI-powered assistant 
                that provides personalized guidance every step of the way.
              </p>
              
              <div style={responsiveStyles.genieFeatures}>
                {[
                  { icon: Brain, text: 'Understand symptoms & diagnoses' },
                  { icon: Stethoscope, text: 'Get procedure recommendations' },
                  { icon: Users, text: 'Find the best specialists' },
                  { icon: Activity, text: 'Track recovery progress' },
                  { icon: Clock, text: '24/7 assistance available' },
                  { icon: CheckCircle, text: 'Checklists & reminders' }
                ].map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} style={responsiveStyles.genieFeature}>
                      <Icon size={isSmallMobile ? 18 : 20} color="#286CFF" />
                      <span>{feature.text}</span>
                    </div>
                  );
                })}
              </div>
              
              <button style={responsiveStyles.genieBtn}>
                Ask Surgery Genie <ArrowRight size={isSmallMobile ? 18 : 20} style={{ marginLeft: '8px' }} />
              </button>
            </div>
            
            <div style={responsiveStyles.genieIllustration}>
              <svg viewBox="0 0 400 400" style={{ width: '100%', height: 'auto' }}>
                <circle cx="200" cy="200" r="180" fill="#E3EDFF" opacity="0.3"/>
                
                {/* AI Brain/Genie */}
                <g transform="translate(200, 200)">
                  <circle cx="0" cy="0" r="70" fill="url(#genieGrad)"/>
                  <defs>
                    <linearGradient id="genieGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#286CFF', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#5B8BFF', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  
                  {/* Neural network */}
                  <circle cx="-20" cy="-15" r="6" fill="white"/>
                  <circle cx="20" cy="-15" r="6" fill="white"/>
                  <circle cx="0" cy="15" r="6" fill="white"/>
                  <circle cx="-30" cy="15" r="5" fill="white" opacity="0.7"/>
                  <circle cx="30" cy="15" r="5" fill="white" opacity="0.7"/>
                  
                  <line x1="-20" y1="-15" x2="20" y2="-15" stroke="white" strokeWidth="2"/>
                  <line x1="-20" y1="-15" x2="0" y2="15" stroke="white" strokeWidth="2"/>
                  <line x1="20" y1="-15" x2="0" y2="15" stroke="white" strokeWidth="2"/>
                  <line x1="-30" y1="15" x2="0" y2="15" stroke="white" strokeWidth="2"/>
                  <line x1="30" y1="15" x2="0" y2="15" stroke="white" strokeWidth="2"/>
                </g>
                
                {/* Floating info bubbles */}
                <g transform="translate(80, 120)">
                  <circle cx="0" cy="0" r="30" fill="white" opacity="0.9"/>
                  <text x="0" y="5" textAnchor="middle" fontSize="20">📋</text>
                </g>
                
                <g transform="translate(320, 140)">
                  <circle cx="0" cy="0" r="30" fill="white" opacity="0.9"/>
                  <text x="0" y="5" textAnchor="middle" fontSize="20">💊</text>
                </g>
                
                <g transform="translate(100, 280)">
                  <circle cx="0" cy="0" r="28" fill="white" opacity="0.9"/>
                  <text x="0" y="5" textAnchor="middle" fontSize="18">✅</text>
                </g>
                
                <g transform="translate(300, 290)">
                  <circle cx="0" cy="0" r="28" fill="white" opacity="0.9"/>
                  <text x="0" y="5" textAnchor="middle" fontSize="18">📱</text>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      <section style={responsiveStyles.journeySection}>
        <div style={responsiveStyles.container}>
          <h2 style={responsiveStyles.sectionTitle}>Your Complete Care Journey</h2>
          <p style={responsiveStyles.sectionSubtitle}>
            From consultation to recovery, we support you at every step
          </p>
          
          <div style={responsiveStyles.journeyTimeline}>
            {patientJourney.map((item, index) => (
              <div key={index} style={responsiveStyles.journeyStep}>
                <div style={responsiveStyles.journeyIcon}>{item.icon}</div>
                <div style={responsiveStyles.journeyContent}>
                  <h3 style={responsiveStyles.journeyStepTitle}>{item.step}</h3>
                  <p style={responsiveStyles.journeyStepDesc}>{item.description}</p>
                </div>
                {index < patientJourney.length - 1 && !isMobile && (
                  <div style={responsiveStyles.journeyConnector}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={responsiveStyles.whySection}>
        <div style={responsiveStyles.container}>
          <h2 style={responsiveStyles.sectionTitle}>Why Choose GEWO Health for Surgery?</h2>
          
          <div style={responsiveStyles.whyGrid}>
            {[
              { icon: Users, title: 'Highly Experienced Surgeons', desc: '500+ certified specialists across all fields' },
              { icon: Activity, title: 'Advanced Technology', desc: 'Minimally invasive & robotic surgery options' },
              { icon: Brain, title: 'AI-Powered Coordination', desc: 'Smart scheduling and seamless care management' },
              { icon: Clock, title: '24/7 Support', desc: 'Round-the-clock assistance via Health Genie' },
              { icon: Heart, title: 'Special Care Programs', desc: 'Dedicated support for elderly & high-risk patients' },
              { icon: Shield, title: 'Transparent Pricing', desc: 'Clear costs with insurance support' }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} style={responsiveStyles.whyCard}>
                  <div style={responsiveStyles.whyIcon}>
                    <Icon size={isSmallMobile ? 28 : isMobile ? 30 : 32} color="#286CFF" />
                  </div>
                  <h3 style={responsiveStyles.whyTitle}>{item.title}</h3>
                  <p style={responsiveStyles.whyDesc}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={responsiveStyles.testimonialsSection}>
        <div style={responsiveStyles.container}>
          <h2 style={responsiveStyles.sectionTitle}>Success Stories</h2>
          <p style={responsiveStyles.sectionSubtitle}>Real patients, real results</p>
          
          <div style={responsiveStyles.testimonialsGrid}>
            {testimonials.map((testimonial, idx) => (
              <div key={idx} style={responsiveStyles.testimonialCard}>
                <div style={responsiveStyles.testimonialHeader}>
                  <div style={responsiveStyles.testimonialAvatar}>{testimonial.image}</div>
                  <div>
                    <div style={responsiveStyles.testimonialName}>{testimonial.name}</div>
                    <div style={responsiveStyles.testimonialProcedure}>{testimonial.procedure}</div>
                  </div>
                </div>
                
                <div style={responsiveStyles.testimonialRating}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={isSmallMobile ? 14 : 16} fill="#FFD28A" color="#FFD28A" />
                  ))}
                </div>
                
                <p style={responsiveStyles.testimonialComment}>{testimonial.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={responsiveStyles.ctaSection}>
        <div style={responsiveStyles.container}>
          <div style={responsiveStyles.ctaContent}>
            <h2 style={responsiveStyles.ctaTitle}>Ready to Take the Next Step?</h2>
            <p style={responsiveStyles.ctaSubtitle}>
              Connect with our specialists and get personalized guidance for your healthcare journey
            </p>
            <div style={responsiveStyles.ctaButtons}>
              <button style={responsiveStyles.ctaButton}>
                Book Consultation <ArrowRight size={isSmallMobile ? 18 : 20} style={{ marginLeft: '8px' }} />
              </button>
              <button style={responsiveStyles.ctaButtonSecondary}>
                Talk to a Surgery Expert
              </button>
              <button style={responsiveStyles.ctaButtonOutline}>
                Ask the Surgery Genie
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const getResponsiveStyles = (isMobile, isSmallMobile) => ({
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },
  heroSection: {
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)',
    paddingTop: isSmallMobile ? '130px' : isMobile ? '140px' : '120px',
    paddingBottom: isSmallMobile ? '40px' : isMobile ? '50px' : '80px',
    paddingLeft: isSmallMobile ? '16px' : isMobile ? '20px' : '0',
    paddingRight: isSmallMobile ? '16px' : isMobile ? '20px' : '0',
    position: 'relative',
    overflow: 'hidden',
    color: 'white'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isSmallMobile ? '0 16px' : isMobile ? '0 20px' : '0 24px'
  },
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1
  },
  heroTitle: {
    fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
    fontWeight: '700',
    lineHeight: '1.2',
    marginBottom: isSmallMobile ? '12px' : '16px',
    padding: isSmallMobile ? '0 8px' : '0',
    marginTop: '40px',
  },
  heroSubtitle: {
    fontSize: isSmallMobile ? '18px' : isMobile ? '20px' : '24px',
    fontWeight: '600',
    marginBottom: isSmallMobile ? '10px' : '12px',
    opacity: 0.95,
    padding: isSmallMobile ? '0 8px' : '0'
  },
  heroDescription: {
    fontSize: isSmallMobile ? '14px' : isMobile ? '16px' : '18px',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '32px' : '40px',
    opacity: 0.9,
    padding: isSmallMobile ? '0 8px' : '0',
    lineHeight: '1.6'
  },
  searchWrapper: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: isSmallMobile ? '0 8px' : '0'
  },
  searchBar: {
    background: 'white',
    borderRadius: isSmallMobile ? '12px' : '16px',
    padding: isSmallMobile ? '6px 6px 6px 16px' : '8px 8px 8px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: isSmallMobile ? '8px' : '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    flexDirection: isMobile ? 'column' : 'row'
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: isSmallMobile ? '14px' : '16px',
    padding: isSmallMobile ? '10px 0' : '12px 0',
    fontFamily: 'inherit',
    width: isMobile ? '100%' : 'auto'
  },
  searchButton: {
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: isSmallMobile ? '10px' : '12px',
    padding: isSmallMobile ? '10px 24px' : '12px 28px',
    fontSize: isSmallMobile ? '14px' : '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: isMobile ? '100%' : 'auto',
    whiteSpace: 'nowrap'
  },
  heroIllustration: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none'
  },
  categoriesSection: {
    padding: isSmallMobile ? '40px 0' : isMobile ? '50px 0' : '60px 0',
    background: '#F8FAFC'
  },
  sectionTitle: {
    fontSize: isSmallMobile ? '24px' : isMobile ? '28px' : '36px',
    fontWeight: '700',
    color: '#2D3748',
    textAlign: 'center',
    marginBottom: isSmallMobile ? '8px' : '12px',
    padding: isSmallMobile ? '0 8px' : '0'
  },
  sectionSubtitle: {
    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '32px' : '40px',
    padding: isSmallMobile ? '0 8px' : '0'
  },
  categoriesGrid: {
    display: 'grid',
    gridTemplateColumns: isSmallMobile ? 'repeat(3, 1fr)' : isMobile ? 'repeat(4, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: isSmallMobile ? '12px' : '16px'
  },
  categoryCard: {
    background: 'white',
    borderRadius: isSmallMobile ? '12px' : '16px',
    padding: isSmallMobile ? '16px 8px' : isMobile ? '20px 12px' : '24px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '2px solid',
    borderColor: '#E2E8F0'
  },
  categoryIcon: {
    fontSize: isSmallMobile ? '28px' : isMobile ? '32px' : '36px',
    marginBottom: isSmallMobile ? '8px' : '12px'
  },
  categoryName: {
    fontSize: isSmallMobile ? '11px' : isMobile ? '12px' : '13px',
    fontWeight: '600',
    color: '#2D3748',
    lineHeight: '1.3'
  },
  surgeriesSection: {
    padding: isSmallMobile ? '40px 0' : isMobile ? '50px 0' : '60px 0',
    background: '#FFFFFF'
  },
  surgeriesHeader: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    gap: isMobile ? '16px' : '0',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
  },
  compareBtn: {
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: isSmallMobile ? '10px 20px' : '12px 24px',
    fontSize: isSmallMobile ? '13px' : '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: isMobile ? '100%' : 'auto'
  },
  surgeriesGrid: {
    display: 'grid',
    gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: isSmallMobile ? '16px' : isMobile ? '20px' : '24px'
  },
  surgeryCard: {
    background: 'white',
    border: '1px solid #E2E8F0',
    borderRadius: isSmallMobile ? '12px' : '16px',
    padding: isSmallMobile ? '20px' : isMobile ? '22px' : '24px',
    transition: 'all 0.3s ease'
  },
  surgeryHeader: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    gap: isMobile ? '12px' : '0',
    marginBottom: isSmallMobile ? '12px' : '16px'
  },
  surgeryName: {
    fontSize: isSmallMobile ? '16px' : isMobile ? '17px' : '18px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: isSmallMobile ? '6px' : '8px',
    lineHeight: '1.3'
  },
  surgeryDescription: {
    fontSize: isSmallMobile ? '13px' : '14px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  compareCheckbox: {
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    alignItems: 'center',
    gap: isMobile ? '8px' : '4px',
    alignSelf: isMobile ? 'flex-start' : 'auto'
  },
  checkbox: {
    width: isSmallMobile ? '18px' : '20px',
    height: isSmallMobile ? '18px' : '20px',
    cursor: 'pointer'
  },
  compareLabel: {
    fontSize: isSmallMobile ? '11px' : '12px',
    color: '#64748B'
  },
  surgeryBenefits: {
    marginBottom: isSmallMobile ? '12px' : '16px'
  },
  benefitLabel: {
    fontSize: isSmallMobile ? '11px' : '12px',
    fontWeight: '600',
    color: '#2D3748',
    marginBottom: isSmallMobile ? '6px' : '8px'
  },
  benefitTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: isSmallMobile ? '6px' : '8px'
  },
  benefitTag: {
    background: '#E8F5F1',
    color: '#28C08A',
    padding: isSmallMobile ? '3px 10px' : '4px 12px',
    borderRadius: '12px',
    fontSize: isSmallMobile ? '11px' : '12px',
    fontWeight: '600'
  },
  surgeryMeta: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isSmallMobile ? '10px' : isMobile ? '12px' : '16px',
    marginBottom: isSmallMobile ? '12px' : '16px'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: isSmallMobile ? '12px' : '13px',
    color: '#64748B'
  },
  knowMoreBtn: {
    width: '100%',
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: isSmallMobile ? '8px' : '10px',
    padding: isSmallMobile ? '10px' : '12px',
    fontSize: isSmallMobile ? '13px' : '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: isMobile ? '16px' : '0'
  },
  compareModal: {
    background: 'white',
    borderRadius: isSmallMobile ? '16px' : '24px',
    padding: isSmallMobile ? '20px' : isMobile ? '24px' : '32px',
    maxWidth: isMobile ? '100%' : '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    width: isMobile ? '100%' : 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: isSmallMobile ? '16px' : '24px'
  },
  modalTitle: {
    fontSize: isSmallMobile ? '20px' : isMobile ? '24px' : '28px',
    fontWeight: '700',
    color: '#2D3748'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px'
  },
  compareTable: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  tableHeader: {
    background: '#F8FAFC',
    padding: isSmallMobile ? '12px 8px' : isMobile ? '14px 12px' : '16px',
    textAlign: 'left',
    fontSize: isSmallMobile ? '12px' : isMobile ? '13px' : '14px',
    fontWeight: '700',
    color: '#2D3748',
    borderBottom: '2px solid #E2E8F0'
  },
  tableCell: {
    padding: isSmallMobile ? '12px 8px' : isMobile ? '14px 12px' : '16px',
    fontSize: isSmallMobile ? '12px' : isMobile ? '13px' : '14px',
    color: '#2D3748',
    borderBottom: '1px solid #E2E8F0'
  },
  genieSection: {
    padding: isSmallMobile ? '40px 0' : isMobile ? '60px 0' : '80px 0',
    background: 'linear-gradient(180deg, #F8FAFC 0%, #E3EDFF 100%)'
  },
  genieGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: isSmallMobile ? '32px' : isMobile ? '40px' : '60px',
    alignItems: 'center'
  },
  genieContent: {
    textAlign: isMobile ? 'center' : 'left'
  },
  genieBadge: {
    display: 'inline-block',
    background: '#286CFF',
    color: 'white',
    padding: isSmallMobile ? '5px 12px' : '6px 16px',
    borderRadius: '20px',
    fontSize: isSmallMobile ? '10px' : '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    marginBottom: isSmallMobile ? '16px' : '20px'
  },
  genieTitle: {
    fontSize: isSmallMobile ? '24px' : isMobile ? '28px' : '36px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: isSmallMobile ? '12px' : '16px',
    lineHeight: '1.2'
  },
  genieDescription: {
    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
    color: '#64748B',
    lineHeight: '1.6',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '28px' : '32px'
  },
  genieFeatures: {
    display: 'grid',
    gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: isSmallMobile ? '12px' : '16px',
    marginBottom: isSmallMobile ? '24px' : '32px'
  },
  genieFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: isSmallMobile ? '10px' : '12px',
    fontSize: isSmallMobile ? '13px' : '14px',
    color: '#2D3748',
    fontWeight: '500',
    justifyContent: isMobile ? 'center' : 'flex-start'
  },
  genieBtn: {
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: isSmallMobile ? '12px 24px' : isMobile ? '14px 28px' : '16px 32px',
    fontSize: isSmallMobile ? '14px' : '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    width: isMobile ? '100%' : 'auto',
    justifyContent: 'center'
  },
  genieIllustration: {
    display: 'flex',
    justifyContent: 'center'
  },
  journeySection: {
    padding: isSmallMobile ? '40px 0' : isMobile ? '60px 0' : '80px 0',
    background: '#FFFFFF'
  },
  journeyTimeline: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'flex-start',
    position: 'relative',
    marginTop: isSmallMobile ? '24px' : isMobile ? '32px' : '40px',
    gap: isMobile ? '24px' : '0'
  },
  journeyStep: {
    flex: isMobile ? 'none' : 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    width: isMobile ? '100%' : 'auto'
  },
  journeyIcon: {
    fontSize: isSmallMobile ? '36px' : isMobile ? '40px' : '48px',
    marginBottom: isSmallMobile ? '12px' : '16px'
  },
  journeyContent: {
    textAlign: 'center'
  },
  journeyStepTitle: {
    fontSize: isSmallMobile ? '13px' : '14px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: isSmallMobile ? '6px' : '8px'
  },
  journeyStepDesc: {
    fontSize: isSmallMobile ? '11px' : '12px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  journeyConnector: {
    position: 'absolute',
    top: isMobile ? 'auto' : '24px',
    left: isMobile ? '50%' : '50%',
    bottom: isMobile ? '-12px' : 'auto',
    width: isMobile ? '2px' : '100%',
    height: isMobile ? '24px' : '2px',
    background: '#E2E8F0',
    zIndex: -1,
    transform: isMobile ? 'translateX(-50%)' : 'none'
  },
  whySection: {
    padding: isSmallMobile ? '40px 0' : isMobile ? '60px 0' : '80px 0',
    background: '#F8FAFC'
  },
  whyGrid: {
    display: 'grid',
    gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
    gap: isSmallMobile ? '16px' : isMobile ? '20px' : '32px',
    marginTop: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
  },
  whyCard: {
    background: 'white',
    borderRadius: isSmallMobile ? '12px' : '16px',
    padding: isSmallMobile ? '24px 16px' : isMobile ? '28px 20px' : '32px 24px',
    textAlign: 'center'
  },
  whyIcon: {
    width: isSmallMobile ? '56px' : isMobile ? '60px' : '64px',
    height: isSmallMobile ? '56px' : isMobile ? '60px' : '64px',
    background: '#E3EDFF',
    borderRadius: isSmallMobile ? '12px' : '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  },
  whyTitle: {
    fontSize: isSmallMobile ? '16px' : isMobile ? '17px' : '18px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: isSmallMobile ? '10px' : '12px'
  },
  whyDesc: {
    fontSize: isSmallMobile ? '13px' : '14px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  testimonialsSection: {
    padding: isSmallMobile ? '40px 0' : isMobile ? '60px 0' : '80px 0',
    background: '#FFFFFF'
  },
  testimonialsGrid: {
    display: 'grid',
    gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? '1fr' : 'repeat(3, 1fr)',
    gap: isSmallMobile ? '16px' : isMobile ? '20px' : '32px',
    marginTop: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
  },
  testimonialCard: {
    background: '#F8FAFC',
    borderRadius: isSmallMobile ? '12px' : '16px',
    padding: isSmallMobile ? '20px' : isMobile ? '22px' : '24px'
  },
  testimonialHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: isSmallMobile ? '12px' : '16px',
    marginBottom: isSmallMobile ? '12px' : '16px'
  },
  testimonialAvatar: {
    fontSize: isSmallMobile ? '32px' : isMobile ? '36px' : '40px'
  },
  testimonialName: {
    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
    fontWeight: '700',
    color: '#2D3748'
  },
  testimonialProcedure: {
    fontSize: isSmallMobile ? '12px' : '13px',
    color: '#64748B'
  },
  testimonialRating: {
    display: 'flex',
    gap: '4px',
    marginBottom: isSmallMobile ? '12px' : '16px'
  },
  testimonialComment: {
    fontSize: isSmallMobile ? '13px' : '14px',
    color: '#2D3748',
    lineHeight: '1.6',
    fontStyle: 'italic'
  },
  ctaSection: {
    padding: isSmallMobile ? '40px 0' : isMobile ? '60px 0' : '80px 0',
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)',
    color: 'white'
  },
  ctaContent: {
    textAlign: 'center'
  },
  ctaTitle: {
    fontSize: isSmallMobile ? '24px' : isMobile ? '28px' : '40px',
    fontWeight: '700',
    marginBottom: isSmallMobile ? '12px' : '16px',
    padding: isSmallMobile ? '0 8px' : '0',
    lineHeight: '1.2'
  },
  ctaSubtitle: {
    fontSize: isSmallMobile ? '14px' : isMobile ? '16px' : '18px',
    marginBottom: isSmallMobile ? '24px' : isMobile ? '32px' : '40px',
    opacity: 0.95,
    maxWidth: '700px',
    margin: isSmallMobile ? '0 auto 24px' : isMobile ? '0 auto 32px' : '0 auto 40px',
    padding: isSmallMobile ? '0 8px' : '0',
    lineHeight: '1.6'
  },
  ctaButtons: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isSmallMobile ? '12px' : '16px',
    justifyContent: 'center',
    padding: isSmallMobile ? '0 16px' : '0'
  },
  ctaButton: {
    background: 'white',
    color: '#286CFF',
    border: 'none',
    borderRadius: '12px',
    padding: isSmallMobile ? '12px 24px' : isMobile ? '14px 28px' : '16px 32px',
    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    width: isMobile ? '100%' : 'auto'
  },
  ctaButtonSecondary: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: isSmallMobile ? '12px 24px' : isMobile ? '14px 28px' : '16px 32px',
    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: isMobile ? '100%' : 'auto'
  },
  ctaButtonOutline: {
    background: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '12px',
    padding: isSmallMobile ? '12px 24px' : isMobile ? '14px 28px' : '16px 32px',
    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: isMobile ? '100%' : 'auto'
  }
});

export default SurgeriesProcedures;
